import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  inject,
  AfterViewInit,
  OnDestroy,
  effect,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationStart } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { MenuService, MenuSection } from '../../services/menu.service';
import { NotificationService } from '../../services/notification.service';
import { ThemeService, THEMES_SOMBRES } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { SessionHorsLignePastilleComponent } from '../../../shared/components/session-hors-ligne-pastille/session-hors-ligne-pastille.component';
import { CompanyService } from '../../services/company.service';
import { WarehouseService } from '../../services/warehouse.service';
import { UserService } from '../../services/user.service';
import { ReferenceDataService } from '../../../features/reference-data/services/reference-data.service';
import { PermissionService } from '../../../features/security/services/permission.service';

import { User } from '../../models/user.model';
import { Warehouse } from '../../models/warehouse.model';
import { Company } from '../../models/company.model';
import { environment } from '../../../../environments/environment';
import { OfflineStateService } from '../../offline/services/offline-state.service';
import { PendingQueueService, PendingRequest } from '../../offline/services/pending-queue.service';
import { PinVaultService } from '../../services/pin-vault.service';
import { PinSetupDialogComponent } from '../../../shared/components/pin-setup-dialog/pin-setup-dialog.component';

import { TopbarProfileCollab } from './topbar-profile.collab';
import { TopbarPinCollab } from './topbar-pin.collab';
import { TopbarVerrouCollab } from './topbar-verrou.collab';
import { VerrouPosteAdmin } from '../../services/verrou-poste';
import { TopbarDockCollab } from './topbar-dock.collab';
import { TopbarPasswordCollab } from './topbar-password.collab';
import { TopbarCompanyCollab, SyncLogEntry } from './topbar-company.collab';
import { PwaInstallService } from '@stockflow/offline-core';
import { DialogueInstallationIosComponent } from '../../../shared/components/dialogue-installation-ios/dialogue-installation-ios.component';

import { TopbarProfileModalComponent } from './composants/topbar-profile-modal.component';
import { TopbarPasswordModalComponent } from './composants/topbar-password-modal.component';
import { TopbarPinChangeDialogComponent } from './composants/topbar-pin-change-dialog.component';
import { TopbarDockComponent } from './composants/topbar-dock.component';
import { TopbarSyncPanelComponent } from './composants/topbar-sync-panel.component';
import type { AppTheme, AppLanguage, NavMode } from './topbar.types';

export type { SyncLogEntry } from './topbar-company.collab';
export type { AppTheme, AppLanguage, NavMode } from './topbar.types';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule, TranslateModule, PinSetupDialogComponent, SessionHorsLignePastilleComponent,
    DialogueInstallationIosComponent, TopbarProfileModalComponent, TopbarPasswordModalComponent,
    TopbarPinChangeDialogComponent, TopbarDockComponent, TopbarSyncPanelComponent,
  ],
  templateUrl: './topbar.component.html',
  styleUrls: [
    './styles/topbar-01.css',
    './styles/topbar-02.css',
    './styles/topbar-partage-02.css',
    './styles/topbar-03.css',
    './styles/topbar-partage-03.css',
    './styles/topbar-partage-05.css',
    './styles/topbar-partage-06.css',
    './styles/topbar-04.css',
  ]
})
export class TopbarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() sidebarOpen = false;
  @Output() sidebarToggle = new EventEmitter<void>();
  @Input() currentUser: User | null = null;
  @Output() languageChange = new EventEmitter<string>();

  private router = inject(Router);
  private translate = inject(TranslateService);
  private elementRef = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);

  menuService = inject(MenuService);
  themeService = inject(ThemeService);
  authService = inject(AuthService);
  companyService = inject(CompanyService);
  warehouseService = inject(WarehouseService);
  userService = inject(UserService);
  referenceDataService = inject(ReferenceDataService);
  permissionService = inject(PermissionService);
  private messageService = inject(MessageService);
  offlineState = inject(OfflineStateService);
  private pendingQueue = inject(PendingQueueService);
  private pinVaultService = inject(PinVaultService);
  private notificationService = inject(NotificationService);

  private profileCollab = new TopbarProfileCollab(
    this.userService, this.referenceDataService,
    this.translate, this.messageService, this.pinVaultService,
  );
  private pinCollab = new TopbarPinCollab(
    this.authService, this.pinVaultService,
    this.translate, this.messageService,
  );
  private verrouCollab = new TopbarVerrouCollab(
    inject(VerrouPosteAdmin), this.translate, this.messageService, () => this.managePin(),
  );
  private dockCollab = new TopbarDockCollab(
    this.elementRef, this.sanitizer, this.router,
    this.translate, this.menuService,
  );
  private passwordCollab = new TopbarPasswordCollab(
    this.authService, this.translate, this.messageService,
  );
  private companyCollab = new TopbarCompanyCollab(
    this.companyService, this.offlineState, this.pendingQueue,
    this.translate, this.messageService,
  );

  stockAlertCount = computed(() => {
    const s = this.notificationService.summary();
    return s.lowStock + s.outOfStock;
  });

  // PIN dialog
  showPinDialog = false;
  pinDialogMode: 'setup' | 'reset' = 'setup';

  // Sync panel
  readonly isTauri = !!(environment as Record<string, unknown>)['isTauri'];
  readonly enableAiModule = !!(environment as Record<string, unknown>)['enableAiModule'];
  showSyncPanel = false;
  syncLogs: SyncLogEntry[] = [];
  pendingRequests: PendingRequest[] = [];

  // Theme — `label` est une cle de traduction (tubes `translate` des gabarits).
  themes: AppTheme[] = [
    { id: 'midnight', label: 'TOPBAR.THEMES.MIDNIGHT' },
    { id: 'snow',     label: 'TOPBAR.THEMES.SNOW' },
    { id: 'warm',     label: 'TOPBAR.THEMES.WARM' },
    { id: 'ocean',    label: 'TOPBAR.THEMES.OCEAN' },
    { id: 'lavender', label: 'TOPBAR.THEMES.LAVENDER' }
  ];
  activeTheme: string = localStorage.getItem('app-theme') ?? 'snow';

  // Language
  languages: AppLanguage[] = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English',  flag: '🇺🇸' },
    { code: 'es', label: 'Español',  flag: '🇪🇸' },
    { code: 'ht', label: 'Kreyòl',   flag: '🇭🇹' }
  ];
  currentLang: string = localStorage.getItem('lang') ?? 'fr';

  // Nav mode
  navMode: NavMode = (localStorage.getItem('nav-mode') as NavMode) ?? 'bar';

  // Dropdown state
  openDropdown: string | null = null;
  showQuickActions = false;

  // Dock window
  showWindow = false;
  windowTitle = '';
  windowItems: { name: string; desc: string; gradient: string; svgIcon: string; route: string; }[] = [];

  // Internal
  isAdmin = false;
  selectedCompany: string | null = null;
  currentPlanName: string | null = null;

  // Company switcher (super admin)
  allCompanies: Company[] = [];
  filteredSwitchCompanies: Company[] = [];
  companySwitchSearch = '';

  // Company switcher (regular user — multi-tenant)
  userCompanies: { companyId: number; name: string; sigle?: string }[] = [];
  showUserCompanySwitcher = false;

  get availableWarehouses(): Warehouse[] {
    const seen = new Set<string>();
    return this.warehouseService.warehouses().filter(w => {
      const key = w.name?.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Profile modal
  pmLangOpen = false;
  pmThemeOpen = false;
  showProfileModal = false;
  profileEmail = '';
  profilePhone = '';
  profileAvatarUrl = '';
  profileUsername = '';
  profileContacts: { fullName: string; phoneNumber: string; relationshipId: string | number }[] = [];
  parentalLinks: { id: number; label: string; code?: string }[] = [];
  profileSaving = false;

  // Change password modal
  showPasswordModal = false;
  currentPwd = '';
  newPwd = '';
  confirmPwd = '';
  pwdError = '';
  pwdSaving = false;
  showCurrentPwd = false;
  showNewPwd = false;
  showConfirmPwd = false;

  // PIN change/setup in profile
  userHasPin = false;
  showPinChangeDialog = false;
  pinChangeMode: 'setup' | 'change' = 'setup';
  pinChangeStep: 'current' | 'new' | 'confirm' = 'new';
  pinChangeDigits: string[] = [];
  pinChangeError = '';

  private adminEffect = effect(() => {
    const admin = this.permissionService.isSuperAdmin();
    const initialized = this.permissionService.initialized();
    if (!initialized) return;
    this.isAdmin = admin;
    if (admin) {
      this.companyCollab.loadSwitchCompanies(this);
    } else {
      this.companyCollab.loadUserCompanies(this);
    }
  });

  dashboardDockSvg!: SafeHtml;

  ngOnInit(): void {
    this.selectedCompany = localStorage.getItem('selectedCompanyName');
    this.currentPlanName = localStorage.getItem('selectedCompanyPlan') || null;
    const companyId = localStorage.getItem('selectedCompanyId');
    if (companyId) {
      this.companyService.getById(+companyId).subscribe({
        next: (company: any) => {
          if (!this.selectedCompany) {
            this.selectedCompany = company.name;
            localStorage.setItem('selectedCompanyName', company.name);
          }
          if (company.planName || company.planCode) {
            this.currentPlanName = company.planName || company.planCode;
            localStorage.setItem('selectedCompanyPlan', this.currentPlanName!);
          }
        },
        error: () => {}
      });
    }
    this.applyTheme(this.activeTheme);
    this.applyLang(this.currentLang);
    this.dockCollab.buildDockDataMap();
    this.dashboardDockSvg = this.svgFor(
      '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
      24
    );
    this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe(() => {
      this.closeWindow();
      this.closeAll();
    });
  }

  ngAfterViewInit(): void { this.dockCollab.setupDockMagnification(); }
  ngOnDestroy(): void { this.dockCollab.destroyDockListeners(); }
  ngOnChanges(_changes: SimpleChanges): void {}

  svgFor(svgInner?: string, size = 15): SafeHtml { return this.dockCollab.svgFor(svgInner, size); }
  getDockWindowItems(section: MenuSection) { return this.dockCollab.getDockWindowItems(section); }
  openDockWindow(labelKey: string, key: string): void { this.dockCollab.openDockWindow(labelKey, key, this); }
  closeWindow(): void { this.dockCollab.closeWindow(this); }
  windowItemClick(route: string): void { this.dockCollab.windowItemClick(route, this); }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) { this.closeAll(); }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') { this.closeAll(); this.closeWindow(); return; }
    if (event.key === '/' && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
      event.preventDefault();
      if (this.enableAiModule) { this.navigate('/ai'); }
    }
  }

  toggleDropdown(id: string): void { this.openDropdown = this.openDropdown === id ? null : id; if (this.openDropdown !== null) { this.showQuickActions = false; } }
  closeAll(): void { this.openDropdown = null; this.showQuickActions = false; }
  toggleQuickActions(): void { this.showQuickActions = !this.showQuickActions; if (this.showQuickActions) this.openDropdown = null; }

  setTheme(themeId: string): void { this.activeTheme = themeId; this.applyTheme(themeId); this.closeAll(); }
  private applyTheme(themeId: string): void {
    document.body.setAttribute('data-theme', themeId);
    localStorage.setItem('app-theme', themeId);
    this.themeService.setTheme(THEMES_SOMBRES.has(themeId) ? 'dark' : 'light');
  }

  setLang(code: string): void { this.currentLang = code; this.applyLang(code); this.languageChange.emit(code); this.closeAll(); }
  private applyLang(code: string): void { this.translate.use(code); localStorage.setItem('lang', code); localStorage.setItem('app-language', code); }
  get currentLanguage(): AppLanguage | undefined { return this.languages.find(l => l.code === this.currentLang); }

  setNavMode(mode: NavMode): void { this.navMode = mode; localStorage.setItem('nav-mode', mode); this.closeWindow(); if (mode === 'dock') { this.dockCollab.setupDockMagnification(); } }
  navigate(route: string): void { this.closeAll(); this.router.navigate([route]); }

  getUserInitials(): string {
    if (!this.currentUser) return '?';
    const name = this.currentUser.name || this.currentUser.username || '';
    const parts = name.trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0] || '?').substring(0, 2).toUpperCase();
  }

  logout(): void { this.closeAll(); this.authService.logout().catch(err => console.error('[TopbarComponent] Logout failed', err)); }
  goToProfile(): void { this.profileCollab.goToProfile(this, () => this.closeAll()); }
  closeProfileModal(): void { this.profileCollab.closeProfileModal(this); }
  onProfilePhotoSelected(event: Event): void { this.profileCollab.onProfilePhotoSelected(event, this); }
  addContact(): void { this.profileContacts.push({ fullName: '', phoneNumber: '', relationshipId: '' }); }
  removeContact(index: number): void { this.profileContacts.splice(index, 1); }
  saveProfileModal(): void { this.profileCollab.saveProfileModal(this); }
  get activeThemeLabel(): string { return this.themes.find(t => t.id === this.activeTheme)?.label || ''; }
  getProfileInitials(): string { const name = this.currentUser?.name || ''; const parts = name.trim().split(/\s+/).filter(p => p.length > 0); if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase(); return (parts[0] || '?').substring(0, 2).toUpperCase(); }
  goToCompanyProfile(): void { this.navigate('/company-profile'); }

  openChangePassword(): void { this.passwordCollab.openChangePassword(this, () => this.closeAll()); }
  closePasswordModal(): void { this.passwordCollab.closePasswordModal(this); }
  async submitChangePassword(): Promise<void> { return this.passwordCollab.submitChangePassword(this); }

  // « Installer l'application » : l'invite du navigateur, ou la marche a suivre sur iPad.
  readonly pwa = inject(PwaInstallService);
  showInstallDialog = false;
  async installApp(): Promise<void> {
    this.closeAll();
    if ((await this.pwa.installer()) === 'manuelle') this.showInstallDialog = true;
  }
  verrouiller(): Promise<void> { return this.verrouCollab.verrouiller(() => this.closeAll()); }
  async managePin(): Promise<void> { return this.pinCollab.managePin(this, () => this.closeAll()); }
  onPinSaved(): void { this.pinCollab.onPinSaved(this); }
  openChangePinDialog(): void { this.pinCollab.openChangePinDialog(this); }
  openSetupPinDialog(): void { this.pinCollab.openSetupPinDialog(this); }
  closePinChangeDialog(): void { this.pinCollab.closePinChangeDialog(this); }
  pinChangeEnterDigit(digit: string): void { this.pinCollab.pinChangeEnterDigit(digit, this); }
  pinChangeDeleteDigit(): void { this.pinCollab.pinChangeDeleteDigit(this); }

  createNewCompany(): void { this.closeAll(); this.router.navigate(['/onboarding'], { queryParams: { newCompany: 'true' } }); }
  switchCompany(): void { this.navigate('/select-company'); }
  toggleSidebar(): void { this.sidebarToggle.emit(); }
  selectUserCompany(company: { companyId: number; name: string }): void { this.companyCollab.selectUserCompany(company, this, () => this.closeAll()); }
  selectSwitchCompany(company: Company): void { this.companyCollab.selectSwitchCompany(company, this, () => this.closeAll()); }
  filterSwitchCompanies(): void { this.companyCollab.filterSwitchCompanies(this); }
  selectWarehouse(warehouse: Warehouse): void { this.warehouseService.selectWarehouse(warehouse); this.closeAll(); }
  clearWarehouseSelection(): void { this.warehouseService.clearWarehouse(); this.closeAll(); }
  toggleSyncPanel(): void { this.companyCollab.toggleSyncPanel(this, () => this.closeAll()); }
  closeSyncPanel(): void { this.companyCollab.closeSyncPanel(this); }

  getInitials(name?: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  }

  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    if (this.currentUser) { this.currentUser = { ...this.currentUser, avatarUrl: undefined }; }
  }
}
